import { motion } from 'framer-motion';
import { useState } from 'react';
import GlassButton from '../../../components/base/GlassButton';

export default function LuxuryContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d52gj39fvv2fnl629oig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-b from-deepBlue to-charcoal overflow-hidden">
      {/* Subtle Background Particles */}
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="particle absolute"
            style={{
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
            Let's Work <span className="gradient-text">Together</span>
          </h2>
          <p className="text-white/70 text-lg font-body max-w-2xl mx-auto">
            Let's craft something extraordinary together. Get in touch to discuss your project.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="space-y-6"
          >
            <div className="glass-panel p-8 rounded-2xl glow-gold">
              <h3 className="text-2xl font-display font-bold text-white mb-6">Get in Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-gold"></i>
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-white mb-1 text-sm">Location</h4>
                    <p className="text-white/70 text-sm font-body">Fateh Pur, Okara, Pakistan</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-gold"></i>
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-white mb-1 text-sm">Phone</h4>
                    <a href="tel:+923044997673" className="text-white/70 hover:text-gold smooth-transition text-sm font-body cursor-pointer">
                      +92 304 4997 673
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-gold"></i>
                  </div>
                  <div>
                    <h4 className="font-display font-bold text-white mb-1 text-sm">Email</h4>
                    <a href="mailto:fareedasad5773@gmail.com" className="text-white/70 hover:text-gold smooth-transition text-sm font-body cursor-pointer">
                      fareedasad5773@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <h4 className="font-display font-bold text-white mb-4 text-sm">Connect on Social Media</h4>
                <div className="flex space-x-3">
                  <a
                    href="https://www.instagram.com/mafgraphics/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center smooth-transition hover:scale-110 cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl text-gold"></i>
                  </a>
                  <a
                    href="https://www.facebook.com/maf.graphics"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center smooth-transition hover:scale-110 cursor-pointer"
                  >
                    <i className="ri-facebook-circle-line text-xl text-gold"></i>
                  </a>
                  <a
                    href="https://www.tiktok.com/@maf_graphics_1632"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-panel glow-gold flex items-center justify-center smooth-transition hover:scale-110 cursor-pointer"
                  >
                    <i className="ri-tiktok-line text-xl text-gold"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="glass-panel p-2 rounded-2xl glow-gold overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d54726.89!2d73.2167!3d30.7333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzDCsDQzJzU5LjkiTiA3M8KwMTMnMDAuMSJF!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Fateh Pur, Okara Location"
              ></iframe>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
          >
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="glass-panel p-8 rounded-2xl glow-gold">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white font-cta font-semibold mb-2 text-sm">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg glass-panel text-white placeholder-white/50 focus:glow-gold-cta smooth-transition outline-none font-body text-sm"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white font-cta font-semibold mb-2 text-sm">
                    Your Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg glass-panel text-white placeholder-white/50 focus:glow-gold-cta smooth-transition outline-none font-body text-sm"
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-cta font-semibold mb-2 text-sm">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-lg glass-panel text-white placeholder-white/50 focus:glow-gold-cta smooth-transition outline-none resize-none font-body text-sm"
                    placeholder="Tell me about your project..."
                  ></textarea>
                  <p className="text-white/50 text-xs mt-1 font-body">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 rounded-full glass-panel glow-gold-cta text-white font-cta font-semibold text-sm smooth-transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap shimmer-cta"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="glass-panel p-4 rounded-lg border border-green-500/50 glow-gold">
                    <p className="text-green-400 text-sm text-center font-body">Message sent successfully! I'll get back to you soon.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="glass-panel p-4 rounded-lg border border-red-500/50">
                    <p className="text-red-400 text-sm text-center font-body">Failed to send message. Please try again.</p>
                  </div>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/923044997673"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 rounded-full glass-panel glow-gold-cta flex items-center justify-center smooth-transition hover:scale-110 z-50 cursor-pointer shimmer-cta float-animation"
        aria-label="Contact on WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl text-gold"></i>
      </a>
    </section>
  );
}
